import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import { PersonaMarkdown } from '../personas/PersonaMarkdown';
import { gymnasiumService } from '../../services/gymnasiumService';
import { Gauntlet, EloRating, Match } from '../../types/gymnasium';
import { FiZap, FiPlay, FiBarChart2, FiAward, FiPlus, FiArrowRight, FiRefreshCw, FiEye, FiCpu, FiHome } from 'react-icons/fi';
import GymSubHeader from './GymSubHeader';

const GymnasiumLanding: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [gauntlets, setGauntlets] = useState<Gauntlet[]>([]);
  const [rankings, setRankings] = useState<EloRating[]>([]);
  const [matchCount, setMatchCount] = useState(0);
  const [recentMatches, setRecentMatches] = useState<any[]>([]);
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewMatch, setReviewMatch] = useState<Match | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [gs, rk, ms, rs] = await Promise.all([
          gymnasiumService.listGauntlets(),
          gymnasiumService.getRankings(),
          gymnasiumService.listMatches(),
          gymnasiumService.listRuns(),
        ]);
        setGauntlets(gs);
        setRankings(rk.ratings || []);
        setMatchCount(rk.match_count || 0);
        setRecentMatches(ms.slice(0, 10));
        setRecentRuns(rs.slice(0, 5));
      } catch (err) {
        console.error('Failed to load gymnasium data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleReviewMatch = async (matchId: string) => {
    try {
      const m = await gymnasiumService.getMatch(matchId);
      setReviewMatch(m);
    } catch (err) {
      console.error('Failed to load match:', err);
    }
  };

  const handleRerunMatch = async (matchId: string) => {
    try {
      const m = await gymnasiumService.getMatch(matchId);
      if (m?.contestants?.length === 2) {
        // Navigate to arena with pre-filled state
        navigate('/gymnasium/arena', {
          state: {
            preselect: m.contestants.map((c: any) => c.persona_id),
            prompt: m.prompt,
            category: m.prompt_category,
          },
        });
      }
    } catch (err) {
      console.error('Failed to load match for rerun:', err);
    }
  };

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode
      ? 'bg-gray-800 border-amber-600 shadow-amber-900/10'
      : 'bg-white border-amber-500 shadow-sm'
  }`;

  const medalIcons = ['🥇', '🥈', '🥉'];

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className={`h-20 rounded-xl ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`} />
        <div className={`h-48 rounded-xl ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`} />
      </div>
    );
  }

  // Match review overlay
  if (reviewMatch) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setReviewMatch(null)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <FiArrowRight className="w-4 h-4 rotate-180" />
          </button>
          <h2 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
            Match Review
          </h2>
        </div>

        <div className={sectionClass}>
          <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Prompt</p>
          <PersonaMarkdown content={reviewMatch.prompt} size="small" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reviewMatch.contestants.map(c => {
            const isWinner = reviewMatch.verdict?.winner === c.persona_id;
            return (
              <div key={c.persona_id} className={`${sectionClass} ${isWinner ? 'ring-2 ring-green-500 shadow-[0_0_20px_rgba(34,197,94,0.2)]' : ''}`}>
                {isWinner && <div className="text-center mb-2"><span className="px-3 py-0.5 rounded-full bg-green-500 text-white text-xs font-bold">WINNER</span></div>}
                <div className="flex items-center gap-2 mb-3">
                  <PersonaBadge personaId={c.persona_id} personaName={c.persona_name} size="sm" clickable={false} />
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{c.persona_name.replace(/_/g, ' ')}</span>
                  <div className="flex-1" />
                  <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{c.duration_ms}ms</span>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  <PersonaMarkdown content={c.response} size="small" />
                </div>
                {(reviewMatch as any).auto_judge?.analysis?.[c.persona_id] && (
                  <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-700/40' : 'border-gray-200'}`}>
                    {(reviewMatch as any).auto_judge.analysis[c.persona_id].strengths?.map((s: string, i: number) => (
                      <div key={`s${i}`} className="flex items-start gap-1.5"><span className="text-green-400 text-xs mt-0.5">+</span><span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{s}</span></div>
                    ))}
                    {(reviewMatch as any).auto_judge.analysis[c.persona_id].weaknesses?.map((w: string, i: number) => (
                      <div key={`w${i}`} className="flex items-start gap-1.5"><span className="text-red-400 text-xs mt-0.5">-</span><span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{w}</span></div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {(reviewMatch as any).auto_judge?.summary && (
          <div className={`${sectionClass} border-violet-500/20`}>
            <div className="flex items-center gap-2 mb-2">
              <FiCpu className="w-3.5 h-3.5 text-violet-400" />
              <p className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>Judge Analysis</p>
            </div>
            <PersonaMarkdown content={(reviewMatch as any).auto_judge.summary} size="small" />
          </div>
        )}

        {reviewMatch.verdict && (
          <div className={`text-center text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Verdict: {reviewMatch.verdict.method?.replace(/_/g, ' ')} · {new Date(reviewMatch.verdict.voted_at).toLocaleString()}
          </div>
        )}

        <div className="flex justify-center gap-3">
          <button
            onClick={() => handleRerunMatch(reviewMatch.id)}
            className={`px-4 py-2 text-sm rounded-lg flex items-center gap-2 ${darkMode ? 'bg-violet-600 hover:bg-violet-500' : 'bg-violet-500 hover:bg-violet-600'} text-white`}
          >
            <FiRefreshCw className="w-4 h-4" /> Re-run This Match
          </button>
          <button onClick={() => setReviewMatch(null)} className={`px-4 py-2 text-sm rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <GymSubHeader
        icon={<FiHome className="w-4 h-4" />}
        title="Hub"
        subtitle="Overview of your gymnasium activity"
      />

      {/* Leaderboard + Recent Matches */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Leaderboard */}
        <div className={sectionClass}>
          <div className="flex items-center justify-between mb-3">
            <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Leaderboard</h2>
            {rankings.length > 5 && (
              <button onClick={() => navigate('/gymnasium/rankings')} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1">
                Full Rankings <FiArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
          {rankings.length === 0 ? (
            <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No matches yet — start an arena match!</p>
          ) : (
            <div className="space-y-2">
              {rankings.slice(0, 5).map((r, i) => (
                <div key={r.persona_id} className={`flex items-center gap-3 p-2.5 rounded-lg ${
                  i < 3 ? (darkMode ? 'bg-amber-900/10 border border-amber-700/20' : 'bg-amber-50 border border-amber-200') : (darkMode ? 'bg-gray-800/50' : 'bg-gray-50')
                }`}>
                  <span className="text-lg w-6 text-center">{medalIcons[i] || `${i + 1}.`}</span>
                  <PersonaBadge personaId={r.persona_id} personaName={r.name} size="xs" clickable={false} />
                  <span className={`text-xs truncate flex-1 min-w-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {r.name.replace(/_/g, ' ')}
                  </span>
                  <span className={`text-sm font-bold shrink-0 ${i === 0 ? 'text-amber-400' : darkMode ? 'text-amber-300' : 'text-amber-600'}`}>{r.elo}</span>
                  <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {r.wins}W {r.losses}L
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Matches */}
        <div className={sectionClass}>
          <h2 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Recent Matches ({recentMatches.length})
          </h2>
          {recentMatches.length === 0 ? (
            <div className="text-center py-6">
              <p className={`text-sm mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No matches yet</p>
              <button onClick={() => navigate('/gymnasium/arena')} className="text-xs text-amber-400 hover:text-amber-300">
                Start your first match →
              </button>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {recentMatches.map((m: any) => (
                <div
                  key={m.match_id}
                  className={`flex items-center gap-2 p-2.5 rounded-lg ${darkMode ? 'bg-gray-800/40 hover:bg-gray-700/40' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                >
                  <span className={`text-xs flex-1 truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {(m.persona_names || []).map((n: string) => n.replace(/_/g, ' ')).join(' vs ')}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${
                    m.winner === 'tie' ? 'bg-gray-600/30 text-gray-400' :
                    m.winner === 'void' ? 'bg-red-600/20 text-red-400' :
                    m.winner ? 'bg-green-600/20 text-green-400' :
                    'bg-amber-600/20 text-amber-400'
                  }`}>
                    {m.winner === 'tie' ? 'Tie' : m.winner === 'void' ? 'Void' : m.winner ? 'Decided' : 'Pending'}
                  </span>
                  <button onClick={() => handleReviewMatch(m.match_id)} className={`text-[10px] shrink-0 ${darkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-500'}`}>
                    <FiEye className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleRerunMatch(m.match_id)} className={`text-[10px] shrink-0 ${darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-500'}`}>
                    <FiRefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Gauntlet Runs */}
      {recentRuns.length > 0 && (
        <div className={sectionClass}>
          <h2 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Recent Gauntlet Runs
          </h2>
          <div className="space-y-1.5">
            {recentRuns.map((r: any) => (
              <button
                key={r.run_id}
                onClick={() => navigate(`/gymnasium/runs/${r.run_id}`)}
                className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all ${
                  darkMode ? 'bg-gray-800/40 hover:bg-gray-700/40' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <PersonaBadge personaId={r.persona_id} personaName={r.persona_name || r.persona_id?.slice(0, 8)} size="xs" clickable={false} />
                <span className={`text-xs flex-1 truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {(r.gauntlet_name || '').replace(/_/g, ' ')}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  r.status === 'completed' ? 'bg-green-600/20 text-green-400' :
                  r.status === 'failed' ? 'bg-red-600/20 text-red-400' :
                  'bg-amber-600/20 text-amber-400'
                }`}>
                  {r.status === 'completed' && r.pass_rate != null ? `${Math.round(r.pass_rate * 100)}%` : r.status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Gauntlet Library */}
      <div className={sectionClass}>
        <div className="flex items-center justify-between mb-3">
          <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Gauntlet Library</h2>
          <button
            onClick={() => navigate('/gymnasium/gauntlets')}
            className={`text-xs flex items-center gap-1 ${darkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-500'}`}
          >
            <FiPlus className="w-3 h-3" /> Create Gauntlet
          </button>
        </div>
        {gauntlets.length === 0 ? (
          <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            No gauntlets yet — create your first scenario pack!
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {gauntlets.slice(0, 6).map(g => (
              <button
                key={g.id}
                onClick={() => navigate(`/gymnasium/gauntlets/${g.id}`)}
                className={`text-left p-3 rounded-lg transition-all ${
                  darkMode
                    ? 'bg-gray-800/50 hover:bg-amber-900/20 border border-gray-700/30 hover:border-amber-500/30'
                    : 'bg-gray-50 hover:bg-amber-50 border border-gray-200 hover:border-amber-300'
                }`}
              >
                <p className={`text-sm font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {g.name.replace(/_/g, ' ')}
                </p>
                <p className={`text-[10px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {g.scenario_count} scenarios
                </p>
                {g.tags.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {g.tags.slice(0, 3).map(t => (
                      <span key={t} className="px-1.5 py-0.5 text-[9px] rounded bg-amber-600/20 text-amber-300">{t}</span>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GymnasiumLanding;
